import type { ComponentDocs } from '../component-docs.js';
import type { CaosFieldMeta } from './field-renderer.js';

/**
 * What `<caos-field-renderer>` says about itself.
 *
 * IT DRAWS WHATEVER IT IS HANDED. Give it a field's metadata — the kernel storage type plus its
 * config — a value, and a mode, and it picks the control. The dispatch is an exhaustive switch over
 * the kernel's own `StorageType` union: add a storage kind to the kernel without adding a case
 * here and this library stops compiling. So the list below of what it can and cannot edit is not a
 * summary that may have drifted; it is the switch.
 *
 * WHAT IT WILL SHOW BUT WILL NOT LET YOU EDIT. Two different reasons, and they are worth telling
 * apart. `Formula`, `Rollup` and `AutoNumber` are never client-writable — the kernel says so — and
 * edit mode draws the value behind a padlock because that is the truth about the field.
 * `ZonedDateTime`, `JSON` and `Localizable` get the same padlock for a different reason: no editor
 * primitive for them exists in this library yet. If you are looking for the gap in the field
 * library, those three are it.
 *
 * `Time` and `DateTime` were on that second list and are not any more. CAOS-600 gave the date field
 * time and datetime presentations, so a time of day edits as a time and a `DateTime` edits as a day
 * AND a time — it used to edit by its date part alone, which wrote midnight over whatever time the
 * value held. CAOS-1673 then removed `Time` from the inline affordance list, where it had been left
 * behind: it was editable in a form and frozen on the record page. CAOS-1678 then fixed how all three
 * COMMIT in place. The inline affordance took the control's own `change` as the signal that the person
 * had finished, which is true of a text box and false of a segmented one: the platform reports a date,
 * time or datetime as changed once the value is COMPLETE, so editing the hour of an existing time saved
 * and closed before the minute could be reached, and Escape had nothing left to cancel. All three now
 * commit on the way out, which is what this component's own contract always said they did.
 *
 * `Overridable` resolves to the leaf type of its computed side's return type and draws THAT leaf's
 * editable control, which is the override column. A return type with no leaf in scope falls back
 * to a text control.
 *
 * THE VALUE CANNOT BE PLACED, AND THAT IS WHY THE EXAMPLES BELOW ARE HOSTED. `field`, `value`,
 * `options`, `search`, `refDisplay` and `refHref` are properties; none of them is observed as an
 * attribute. A placement writes a string, a number or a boolean as an ATTRIBUTE, so an example
 * that put `value: 'Meridian Supply'` in its inputs would print convincing code and draw an
 * empty field. An object-valued input — `field`, or a multi-select's array value — is set as a
 * property and works. So the metadata is placed and the scalar values are supplied by the page
 * around the example, which the example says out loud rather than papering over.
 *
 * TWO THINGS THE SOURCE SETTLES THAT A NAME WOULD NOT. `required` is in `observedAttributes` but
 * nothing reads it: requiredness comes from `field.required` and is forwarded to whichever control
 * is drawn, so setting the attribute does nothing and it is not listed as an input. And `numeric`
 * is set by the piece ON ITSELF for a `Number` or an `AutoNumber`, which is what puts the value in
 * tabular figures — an author has no reason to set it.
 */

/** The field metadata the examples hand over. Typed, so a rename on the piece breaks the compile here. */
const ACCOUNT: CaosFieldMeta = { storageType: 'Text', label: 'Account', required: true };
const TOTAL: CaosFieldMeta = { storageType: 'Number', format: 'currency', label: 'Total', scale: 2 };
const EXTENDED: CaosFieldMeta = { storageType: 'Formula', label: 'Extended price', formulaReturnType: 'currency' };
const MARGIN: CaosFieldMeta = { storageType: 'Number', format: 'percent', label: 'Margin', scale: 1 };
const SITE: CaosFieldMeta = { storageType: 'Geolocation', label: 'Site' };

/** Hand the same value to every renderer on the stage — each example uses one field's worth. */
const supply = (value: unknown) => (stage: HTMLElement): void => {
  stage.querySelectorAll('caos-field-renderer').forEach((node) => {
    node.value = value;
  });
};

const docs: ComponentDocs = {
  aliases: ['field', 'field component', 'dynamic field', 'typed field', 'field output', 'record field'],

  answers: [
    'draw a field without knowing its type until the record is read',
    'show the same field reading and editing',
    'edit a value in place on a record detail',
    'show that a value is hidden from this person',
    'show that a value is calculated and cannot be typed over',
  ],

  placements: ['recordPage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'field',
      // Property only: it is the field's metadata object, and an attribute is a string.
      type: 'json',
      description:
        "The field's metadata — `storageType` plus `label`, `required`, `format`, `scale` and " +
        '`formulaReturnType` where they apply. Everything the piece draws follows from this. With nothing set ' +
        'it draws nothing at all. A `Text` field in the `rich` format reads as formatted text, sanitised by the ' +
        'allow-list the Text kind documents, and edits with a formatting toolbar. THE FORMATS THIS PIECE HONOURS ' +
        'ARE NAMED, not passed through: `long` reads and edits as several lines; `email`, `phone` and `url` read as ' +
        'the thing they reach, a mail, a dial or a link; `currency` and `percent` read as money and as a ' +
        'proportion; `relative` reads a day or an instant as "3 days ago". A day, a time of day and an instant each ' +
        'read in ONE named display chosen here rather than per page, so the same stored value reads the same way on ' +
        'a record page, in a list and in a document. A day and a time of day carry no zone and are read back in ' +
        'UTC; only an instant is read in the reader\'s own zone. A `url` value is linked only when it ' +
        'is the web or a path within it: the stored text is the whole URL, so a value naming any other scheme ' +
        'is read as text rather than turned into something a click would run, and so is one naming a user ' +
        'before its host, because the host a reader sees is then not the host a click opens. An `email` ' +
        'value is linked only when it is ONE address: everything after a `?` in a mail URL is the message ' +
        'itself, and a comma adds recipients. That is what makes a component per format unnecessary rather ' +
        'than merely discouraged.',
      required: true,
    },
    {
      name: 'value',
      // Property only, and not observed as an attribute — see the header.
      type: 'json',
      description:
        "The value, in whatever shape the field's own type says: a string, a number, a boolean, an ISO date, " +
        'an array for a multi-select, an id for a reference. Set as a property.',
    },
    {
      name: 'mode',
      attribute: 'mode',
      property: true,
      type: 'text',
      options: ['read', 'edit', 'inline'],
      fallback: 'read',
      description:
        '`read` for the value under its label, `edit` for the boxed form control, `inline` for the ' +
        'record-detail affordance — the value in place, click it to edit, commit on blur or Enter, cancel on ' +
        'Escape. Anything else reads as `read`.',
      default: 'read',
    },
    {
      name: 'masked',
      attribute: 'masked',
      property: true,
      type: 'boolean',
      description:
        'This person may not see the value. Draws a padlock and dots in every mode, and never draws the value ' +
        'or an editor — including in edit mode, which is checked before the control is chosen.',
      default: false,
    },
    {
      name: 'options',
      // Property only: a list of value/label pairs.
      type: 'json',
      description:
        'The selectable values for a `Select` or `MultiSelect`, as `{ value, label }` pairs. They are also what ' +
        'read mode uses to turn a stored value back into its label, so a select without them reads as its raw value.',
    },
    {
      name: 'search',
      // Property only: the seam is a function. The closed input-type set has no member for one.
      type: 'json',
      description:
        'The result source handed on to the lookup for a `Reference` field — `(query) => options`. Scoping it to ' +
        'what the current person may read is this function\'s job, not the renderer\'s.',
    },
    {
      name: 'refDisplay',
      // Property only. NOTE: a string set through a placement is written as an ATTRIBUTE, which this
      // piece does not observe — see the header. Set it in code.
      type: 'text',
      description:
        "A `Reference` field's linked-record name, so the link reads as the record rather than as its id. Set as " +
        'a property; the attribute spelling is not observed.',
    },
    {
      name: 'refHref',
      type: 'text',
      description:
        "Where a `Reference` field's link goes. Set as a property; the attribute spelling is not observed.",
    },
    {
      name: 'richTextToolbar',
      // Property only: a list of button declarations.
      type: 'json',
      description:
        'The buttons packages add to the editor of a `Text` field in the `rich` format, each `{ key, label, icon, group, ' +
        'order, sourceKey }` with exactly one of `insert` (markup placed at the cursor, sanitised) or `command` (a name ' +
        "raised in `caos-rich-text-command`). They come after the platform's own buttons. The workspace reads them off " +
        "installed packages' `rich-text` capability claims. Set as a property.",
    },
  ],

  events: [
    {
      name: 'caos-field-change',
      when:
        'When the control it drew reports a change — a field committed, a box toggled, an option chosen, a ' +
        'lookup re-pointed or cleared — and when an inline edit is committed, either with Enter or by leaving ' +
        'the editor. A date, time or datetime edited in place commits only on the way out: the platform calls ' +
        'one of those changed as soon as every segment holds something, which on a field that already had a ' +
        'value is after the first segment rather than when the person has finished. Never when the page sets ' +
        'the value itself.',
      carries: '`{ value }` — the new value, in the field type\'s own shape.',
    },
    {
      name: 'caos-rich-text-command',
      when:
        "When a package's toolbar button that names a `command` is pressed, in the editor of a rich `Text` field. " +
        'Nothing in the platform handles it: it is there for the component the package ships.',
      carries:
        '`{ command, key, sourceKey, selectedText }` — the command name, the button, the package it came from, and ' +
        'the text selected when it was pressed.',
    },
  ],

  // No <slot> anywhere in the shadow root: everything inside is built from the metadata.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-font', controls: 'The typeface of the value and the masked placeholder.' },
    { token: '--caos-font-mono', controls: 'The typeface of the small uppercase label above a read value.' },
    { token: '--caos-font-weight-label', controls: 'The weight of that label.' },
    { token: '--caos-text-body', controls: 'The size of the value.' },
    { token: '--caos-color-text', controls: 'The colour of the value.' },
    { token: '--caos-color-text-muted', controls: 'The label above the value, a blank em dash, the masked dots and the padlock.' },
    { token: '--caos-color-accent', controls: 'The dashed hover cue on an inline value, and the colour a reference reads as.' },
    { token: '--caos-space-1', controls: 'The gap under the label.' },
    { token: '--caos-space-2', controls: 'The gap between the padlock and the value it stands beside.' },
    { token: '--caos-duration-fast', controls: 'How quickly the inline hover cue fades in. Falls back to 120ms.' },
  ],

  // It is driven entirely by its properties: set them and it redraws itself. There is nothing to call.
  parts: [
    { part: 'control', is: 'the field body, and the editor it builds for the field type' },
    {
      part: 'label',
      is: 'the kicker drawn above the value',
      when: 'Not in edit mode, where the control carries its own label and this piece draws none.',
    },
    {
      part: 'value',
      is: 'the value as read, the dash standing in for an empty one included',
      when: 'In read and inline mode. Edit mode draws a control instead, which carries `control` rather than this.',
    },
    {
      part: 'masked',
      is: 'the stand-in drawn where the value is hidden',
      when: 'Only while masked, which is how a value the reader may not see is shown at all.',
    },
    {
      part: 'help',
      is: "the field's own help text, drawn under the control it explains",
      when:
        'Only where somebody is typing: edit mode, and an inline editor while it is open. A field ' +
        'that declares no help text draws nothing, and a masked field draws none either.',
    },
  ],

  methods: [],

  placing:
    'Placed as an item in a page section, one per field. It brings its own label in read and inline mode — the ' +
    'small uppercase kicker above the value — and leaves the label to the control it draws in edit mode, so a ' +
    'section should not add a second one. It holds no data and fetches nothing: the metadata, the value, the ' +
    "options for a select and the search for a reference all come from the page. A reference field's picker " +
    'opens downward inside the field, so the same room-below caution as LookupField applies.',

  whenToUse:
    'The type is not known when the page is written — a record detail, a generated form, an inspector, anything ' +
    'driven by field metadata. Reach for a typed primitive instead when you know at authoring time that the ' +
    'value is text, or a number, or a date. Note before choosing it for a form that several storage kinds are ' +
    'shown behind a padlock rather than edited: the computed ones because they are not writable, and the ' +
    'temporal, JSON and localizable ones because no editor for them exists yet.',

  insteadUse: [
    { name: 'TextField', when: 'You know at authoring time that the value is a line of text.' },
    { name: 'NumberField', when: 'You know it is a number, and you want the min/max/step the metadata cannot carry.' },
    { name: 'DateField', when: 'You know it is a calendar date.' },
    { name: 'LocationField', when: 'You know it is a place, and you want the latitude and longitude boxes without the metadata.' },
    { name: 'PicklistField', when: 'You know it is one of a fixed list, and the list is written into the page rather than read from metadata.' },
    { name: 'LookupField', when: 'You know it is a reference, and you would rather drive the read and edit halves yourself.' },
    { name: 'KeyValueRow', when: 'The value is only ever read, is already a string, and no type-driven formatting is wanted.' },
    { name: 'AccessDeniedPanel', when: 'It is the whole record that is out of reach, not one field on it.' },
  ],

  accessibility:
    'Whatever it draws, it draws the real control, so everything those pieces manage comes with it — the labels, ' +
    'the combobox on a reference, the named group of boxes a multi-select edits with. It adds three things of ' +
    'its own: a multi-select read is a pill container named by the field label, so its values are announced as ' +
    'one list of so many items rather than as loose words; a masked value carries a name saying it is hidden and why, instead of ' +
    'announcing as a row of dots; and an inline value is a `role="button"` in the tab order, named "Edit " and ' +
    'the field name, which is what makes click-to-edit reachable without a mouse. What is left to the author is ' +
    'the field LABEL in the metadata — everything the piece announces is built out of it — and the option labels ' +
    'for a select, since read mode falls back to the stored value when it has none.',

  examples: [
    {
      key: 'read-and-edit',
      label: 'One field, read and edited',
      summary: 'The same text field both ways: the value under its small label, and the boxed control that carries its own.',
      placement: [
        { component: 'caos-field-renderer', inputs: { mode: 'read', field: ACCOUNT } },
        { component: 'caos-field-renderer', inputs: { mode: 'edit', field: ACCOUNT } },
      ],
      host: {
        summary:
          'sets the value on both. A placement writes a string as an attribute, and this piece reads its value ' +
          'only as a property — so the metadata is placed and the value is handed over in code.',
        run: supply('Meridian Supply'),
      },
    },
    {
      key: 'by-type',
      label: 'A different storage kind',
      summary:
        'The same two modes over a currency number instead: read formats it as money and lines it up in tabular ' +
        'figures, edit draws a number control stepped to the scale the metadata declares. Nothing about the page changed.',
      placement: [
        { component: 'caos-field-renderer', inputs: { mode: 'read', field: TOTAL } },
        { component: 'caos-field-renderer', inputs: { mode: 'edit', field: TOTAL } },
      ],
      host: { summary: 'sets the value on both, for the same reason as above.', run: supply(12400) },
    },
    {
      key: 'place',
      label: 'A place, read and edited',
      summary:
        'A location field both ways. Read names each number by its hemisphere, latitude first, so the pair is never ' +
        'two bare numbers to guess the order of. Edit draws a Latitude box and a Longitude box that save together.',
      placement: [
        { component: 'caos-field-renderer', inputs: { mode: 'read', field: SITE } },
        { component: 'caos-field-renderer', inputs: { mode: 'edit', field: SITE } },
      ],
      host: {
        summary: 'sets the same place on both, for the same reason as above.',
        run: supply({ latitude: 29.7604, longitude: -95.3698 }),
      },
    },
    {
      key: 'inline',
      label: 'Edited in place',
      summary:
        'The record-detail mode: the value sits in the running text with a dashed cue under it, and clicking it ' +
        'swaps in the underline editor — commit on Enter or on leaving, cancel on Escape.',
      placement: { component: 'caos-field-renderer', inputs: { mode: 'inline', field: ACCOUNT } },
      host: { summary: 'sets the value, for the same reason as above.', run: supply('Meridian Supply') },
    },
    {
      key: 'computed',
      label: 'Calculated, in edit mode',
      summary:
        'A formula asked to edit: it draws the padlock and the formatted value instead of a control, because the ' +
        'kernel does not let a calculated field be typed over. This is what a form full of fields does with the ones it cannot edit.',
      placement: { component: 'caos-field-renderer', inputs: { mode: 'edit', field: EXTENDED } },
      host: { summary: 'sets the value, for the same reason as above.', run: supply(3150) },
    },
    {
      key: 'masked',
      label: 'Hidden from this person',
      summary:
        'A field the current person may not see: a padlock and dots in place of the value, in read and edit alike, ' +
        'with no value ever put on the page. The only example here that needs nothing from the page around it — ' +
        'there is no value to supply.',
      placement: [
        { component: 'caos-field-renderer', inputs: { mode: 'read', masked: true, field: MARGIN } },
        { component: 'caos-field-renderer', inputs: { mode: 'edit', masked: true, field: MARGIN } },
      ],
    },
  ],
};

export default docs;
